import { Suspense, useEffect, useState } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DetailCheckbox, SingleCheckbox } from "@/components/ui/checkbox"
import { DeprecatedConfirmationDialog } from "@/components/ui/deprecated-confirmation-dialog"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { Input, TextArea } from "@/components/ui/input"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { cn, preventTextInput } from "@/lib/utils"
import { DiagnosisCode } from "@/modules/patients/components/diagnosis-code"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { useLatestDiagnosisCode } from "@/modules/patients/detail/progress-note/hooks/useLatestDiagnosisCode"
import { SYMPTOMS } from "@/modules/patients/detail/therapy-prescription/constants"
import {
  TherapyPrescription,
  therapyPrescriptionAdditionalInformationMaxLength,
  TherapyPrescriptionSchema,
} from "@/modules/patients/detail/therapy-prescription/de/schema/therapy-prescription-schema"
import { deTherapyPrescriptionFragment } from "@/modules/patients/detail/therapy-prescription/de/services/deTherapyPrescriptionFragment"
import { useDeCreateTherapyPrescriptionMutation } from "@/modules/patients/detail/therapy-prescription/de/services/useDeCreateTherapyPrescriptionMutation"
import { useDeDeleteTherapyPrescriptionMutation } from "@/modules/patients/detail/therapy-prescription/de/services/useDeDeleteTherapyPrescriptionMutation"
import { useDeGenerateTherapyPrescriptionDocumentMutation } from "@/modules/patients/detail/therapy-prescription/de/services/useDeGenerateTherapyPrescriptionDocumentMutation"
import { useDeUpdateTherapyPrescriptionMutation } from "@/modules/patients/detail/therapy-prescription/de/services/useDeUpdateTherapyPrescriptionMutation"
import { useTherapyDrawerStoreContext } from "@/modules/patients/detail/therapy-prescription/store/useTherapyDrawerStore"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { Symptom } from "@/types/__generated__/union-types"

interface DeTherapyPrescriptionDrawerProps extends FetchKeyProps {
  progressNoteId: string
}

const DeTherapyPrescriptionDrawer = withErrorBoundary(
  ({ progressNoteId, fetchKey }: DeTherapyPrescriptionDrawerProps) => {
    const { t } = useTranslation()
    const patientId = usePatientParam()
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false)
    const closeDrawer = useDrawerStore((state) => state.closeDrawer)
    const [createTherapyPrescription, isCreating] = useDeCreateTherapyPrescriptionMutation()
    const [updateTherapyPrescription, isUpdating] = useDeUpdateTherapyPrescriptionMutation()
    const [deleteTherapyPrescription] = useDeDeleteTherapyPrescriptionMutation()
    const [generateTherapyPrescriptionDocument, isGenerating] = useDeGenerateTherapyPrescriptionDocumentMutation()
    const therapyPrescriptionId = useTherapyDrawerStoreContext((state) => state.therapyPrescriptionId)
    const therapyPrescriptionRef = useTherapyDrawerStoreContext((state) => state.therapyPrescriptionRef)
    const setTherapyPrescription = useTherapyDrawerStoreContext((state) => state.setTherapyPrescription)
    const { toast } = useToast()
    const isEditMode = !!therapyPrescriptionId
    const isAddMode = !isEditMode && patientId && progressNoteId
    const persistedData = useFragment(deTherapyPrescriptionFragment, therapyPrescriptionRef)
    const updatedAt = persistedData?.updatedAt || persistedData?.createdAt
    const updatedBy = persistedData?.updatedBy || persistedData?.createdBy
    const { diagnosisCode: latestDiagnosisCode } = useLatestDiagnosisCode(progressNoteId)

    const form = useForm<TherapyPrescription>({
      mode: "all",
      resolver: zodResolver(TherapyPrescriptionSchema),
      defaultValues:
        isEditMode && persistedData
          ? {
              ...persistedData,
              symptoms: persistedData.symptoms ? Array.from(persistedData.symptoms) : [],
              treatmentUnit: String(persistedData.treatmentUnit),
              otherSymptoms: persistedData.otherSymptoms ?? "",
              additionalInformation: persistedData.additionalInformation ?? "",
            }
          : {
              therapy: t("patient.therapyPrescription.physiotherapy"),
              requestTherapyReport: false,
              diagnosisCode: latestDiagnosisCode,
              symptoms: [],
              otherSymptoms: "",
              additionalInformation: "",
              frequency: "",
              treatmentUnit: "",
            },
    })

    const {
      register,
      control,
      watch,
      trigger,
      setValue,
      setFocus,
      formState: { errors, isDirty },
    } = form

    useDirtyDrawerMaker(isDirty)

    useEffect(() => {
      const otherSymptomsSubscription = watch((_, { name }) => {
        if (name === "otherSymptoms" || name === "symptoms") {
          void trigger(["symptoms"])
        }
      })
      return () => otherSymptomsSubscription.unsubscribe()
    }, [trigger, watch])

    const onSubmit: SubmitHandler<TherapyPrescription> = (data) => {
      const { additionalInformation, otherSymptoms } = data
      if (!progressNoteId) return

      const formattedData = {
        ...data,
        treatmentUnit: Number(data.treatmentUnit),
        otherSymptoms: otherSymptoms ? otherSymptoms : null,
        additionalInformation: additionalInformation.length > 0 ? additionalInformation : null,
      }

      if (isAddMode) {
        createTherapyPrescription({
          variables: {
            input: {
              therapy: formattedData.therapy,
              treatmentUnit: formattedData.treatmentUnit,
              otherSymptoms: formattedData.otherSymptoms,
              additionalInformation: formattedData.additionalInformation,
              requestTherapyReport: formattedData.requestTherapyReport,
              symptoms: formattedData.symptoms,
              frequency: formattedData.frequency,
              patientId,
              progressNoteId,
              diagnosisCode: formattedData.diagnosisCode,
            },
          },
          onCompleted(response) {
            const therapyPrescriptionId = response.de_createTherapyPrescription.de_TherapyPrescription?.id

            if (therapyPrescriptionId) {
              handleGenerateTherapyPrescriptionDocument(therapyPrescriptionId, data)
            }
          },
          updater(store, data) {
            try {
              const progressNoteRecordProxy = store.get(progressNoteId)
              const therapyPrescription = data?.de_createTherapyPrescription?.de_TherapyPrescription

              if (therapyPrescription?.id) {
                const newRecordProxy = store.get(therapyPrescription.id)
                if (newRecordProxy) {
                  progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "de_therapyPrescription")
                }
              }
            } catch (error) {
              console.error("Error while processing data:", error)
            }
          },
        })
      }

      if (isEditMode) {
        updateTherapyPrescription({
          variables: {
            input: {
              therapy: formattedData.therapy,
              treatmentUnit: formattedData.treatmentUnit,
              otherSymptoms: formattedData.otherSymptoms,
              additionalInformation: formattedData.additionalInformation,
              requestTherapyReport: formattedData.requestTherapyReport,
              symptoms: formattedData.symptoms,
              frequency: formattedData.frequency,
              id: therapyPrescriptionId,
              diagnosisCode: formattedData.diagnosisCode,
            },
          },
          onCompleted(response) {
            const therapyPrescriptionId = response.de_updateTherapyPrescription.de_TherapyPrescription?.id

            if (therapyPrescriptionId) {
              handleGenerateTherapyPrescriptionDocument(therapyPrescriptionId, data)
            }
          },
        })
      }
    }

    const handleCloseDrawer = () => {
      setTherapyPrescription(null, null)
      closeDrawer()
    }

    const handleDelete = () => {
      if (progressNoteId && therapyPrescriptionId) {
        deleteTherapyPrescription({
          variables: {
            input: {
              id: therapyPrescriptionId,
            },
          },
          onCompleted() {
            handleCloseDrawer()
            handleOpenConfirmDialog(false)
            toast({
              description: t("patient.therapyPrescription.deleteMessage.success"),
            })
          },
        })
      }
    }

    const handleOpenConfirmDialog = (isOpen: boolean) => {
      setIsOpenConfirmDialog(isOpen)
    }

    useEffect(() => {
      if (isAddMode) {
        setTimeout(() => {
          setFocus("treatmentUnit")
        }, 200)
      }
    }, [isAddMode, setFocus])

    const handleGenerateTherapyPrescriptionDocument = (id: string, data: TherapyPrescription) => {
      if (id) {
        generateTherapyPrescriptionDocument({
          id,
          diagnosisCode: data.diagnosisCode,
          onCompleted() {
            handleCloseDrawer()
          },
        })
      }
    }

    const sortSymtoms = (checkedSymtoms: Array<Symptom>) => {
      return SYMPTOMS.filter((item) => checkedSymtoms.includes(item))
    }

    return (
      <>
        <Drawer open onOpenChange={handleCloseDrawer}>
          <FormProvider {...form}>
            <DrawerContent>
              <DrawerClose className="top-2 right-2" />
              <DrawerHeader className="pb-4">
                <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                  {t("episodeOfCare.progressNote.deTherapyPrescription")}
                </DrawerTitle>
              </DrawerHeader>
              <DrawerBody>
                <div className="flex flex-col gap-4">
                  <Input
                    {...register("therapy")}
                    error={errors.therapy?.message}
                    translateError={true}
                    label={t("patient.therapyPrescription.therapy")}
                    required
                    testId="therapy-field"
                  />
                  <div className="flex gap-4">
                    <div className="w-2/4">
                      <Input
                        {...register("treatmentUnit")}
                        error={errors.treatmentUnit?.message?.toString()}
                        translateError={true}
                        label={t("patient.therapyPrescription.treatmentUnits")}
                        testId="treatment-units-field"
                        maxLength={2}
                        required
                        onChange={(e) => {
                          setValue("treatmentUnit", preventTextInput(e.target.value), {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }}
                      />
                    </div>
                    <div className="grow basis-2/4">
                      <Input
                        {...register("frequency")}
                        error={errors.frequency?.message}
                        translateError={true}
                        label={t("patient.therapyPrescription.frequency")}
                        required
                        testId="frequency-field"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-between">
                    <span className="text-sm">{t("patient.therapyPrescription.requestTherapyReport")}</span>
                    <div className="-mt-2.5 -mr-0.5">
                      <FormField
                        control={control}
                        name="requestTherapyReport"
                        render={({ field: { value, onChange } }) => (
                          <SingleCheckbox checked={value} onCheckedChange={onChange} />
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-4 pt-8.75">
                  <label className="text-on-surface text-base font-medium">
                    {t("patient.therapyPrescription.reason")}
                  </label>
                  <FormField
                    control={control}
                    name="diagnosisCode"
                    render={({ field: { name, onChange, value }, fieldState: { invalid } }) => (
                      <Suspense fallback={null}>
                        <DiagnosisCode
                          defaultValue={isAddMode ? latestDiagnosisCode : persistedData?.diagnosisCode}
                          name={name}
                          errors={errors}
                          invalid={invalid}
                          value={value}
                          fetchKey={fetchKey}
                          onSelect={onChange}
                        />
                      </Suspense>
                    )}
                  />

                  <div>
                    <div className={cn("rounded-[8px] outline-hidden", errors.symptoms && "outline-critical!")}>
                      <label className="text-xs">{t("patient.therapyPrescription.symptoms")}*</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <FormField
                          control={control}
                          name="symptoms"
                          render={({ field: { onChange, value = [] } }) => (
                            <>
                              {SYMPTOMS.map((item) => (
                                <DetailCheckbox
                                  checked={value.includes(item)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? onChange(sortSymtoms([...value, item]))
                                      : onChange(value.filter((value) => value !== item))
                                  }}
                                  key={item}
                                  testId={item}
                                  className="pr-1.25">
                                  {t(`patient.therapyPrescription.symptom.${item}`)}
                                </DetailCheckbox>
                              ))}
                            </>
                          )}
                        />
                        <Input
                          {...register("otherSymptoms")}
                          wrapperProps={{ className: "mt-2" }}
                          label={t("patient.therapyPrescription.otherSymptoms")}
                        />
                      </div>
                    </div>
                    {errors.symptoms && <TranslatedFieldError errors={errors} name="symptoms" />}
                    {errors.otherSymptoms?.message && !errors.symptoms?.message && (
                      <TranslatedFieldError errors={errors} name="otherSymptoms" />
                    )}
                  </div>
                  <div className="mt-4 gap-4">
                    <TextArea
                      {...register("additionalInformation")}
                      error={errors.additionalInformation?.message}
                      label={t("patient.therapyPrescription.additionalInfo")}
                      translateError
                      textLimit={therapyPrescriptionAdditionalInformationMaxLength}
                    />
                  </div>
                </div>
              </DrawerBody>
              <DrawerFooter className="flex-wrap gap-2" data-testid="therapy-prescription-footer">
                {isEditMode && (
                  <div className="flex gap-2">
                    {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                    <button onClick={() => handleOpenConfirmDialog(true)} data-testid="delete-button">
                      <TrashSimple className="text-on-surface-subdued" />
                    </button>
                  </div>
                )}
                <div className="ml-auto flex gap-2">
                  <Button variant="secondary" onClick={handleCloseDrawer} data-testid="cancel-prescription-button">
                    {t("common.button.cancel")}
                  </Button>
                  <Button
                    disabled={isEditMode && !isDirty}
                    isLoading={isCreating || isUpdating || isGenerating}
                    onClick={() => {
                      FaroInsightsWrapper.pushEvent("documents_therapy_save")
                      form.handleSubmit(onSubmit)()
                    }}
                    data-testid="prescription-save-button">
                    {t("common.button.save")}
                  </Button>
                </div>
              </DrawerFooter>
            </DrawerContent>
          </FormProvider>
        </Drawer>

        <DeprecatedConfirmationDialog
          title={t("patient.therapyPrescription.deleteConfirmTitle")}
          content={t("episodeOfCare.deleteProgressNote.content")}
          open={isOpenConfirmDialog}
          onConfirm={handleDelete}
          confirmVariant="danger"
          confirmText={t("common.button.delete")}
          cancelText={t("common.button.keep")}
          onOpenChange={handleOpenConfirmDialog}
        />
      </>
    )
  },
  "DeTherapyPrescriptionDrawer",
)

export { DeTherapyPrescriptionDrawer }
